import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "~/api/trpc";
import Content from "~/components/layout/Content";
import { Button } from "~/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import dayjs from "dayjs";
import { useUserStore } from "~/stores/user-store";
import { useShallow } from "zustand/react/shallow";
import { Timezone } from '~/types/server-types/Timezone'
import { UserRole } from '~/types/server-types/UserRole'
import { passwordSchema, passwordRequirementsText } from '~/lib/passwordPolicy'

const UserSettings = () => {
  const navigate = useNavigate();
  const trpcUtils = trpc.useUtils();
  const { user } = useUserStore(
    useShallow((state) => ({
      user: state.user,
    }))
  );

  const { mutate: updateUser, isPending } = trpc.user.updateMe.useMutation({
    onSuccess: () => {
      trpcUtils.user.getMe.invalidate();
      trpcUtils.user.getMe.refetch();
      toast.success("Die Änderungen wurden gespeichert.");
    },
    onError: (error) => {
      toast.error(`Fehler beim Speichern: ${error.message}`);
    }
  });

  const [selectedTimezone, setSelectedTimezone] = useState<string>('Europe/Berlin');
  useEffect(() => {
    if(user) {
      setSelectedTimezone(user?.timezone)
    }
  }, [user]);

  const isTimezoneLocked = user?.role === UserRole.USER;

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const { mutate: changeMyPassword, isPending: isChangingPassword } =
    trpc.user.changeMyPassword.useMutation({
      onSuccess: () => {
        toast.success("Dein Passwort wurde geändert.");
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setPasswordError(null);
      },
      onError: (error) => {
        setPasswordError(error.message);
        toast.error(`Fehler beim Ändern des Passworts: ${error.message}`);
      },
    });

  const handleSave = () => {
    if (selectedTimezone) {
      updateUser({ timezone: selectedTimezone as any });
      trpcUtils.user.getMe.refetch();
    }
  };

  const handleChangePassword = () => {
    setPasswordError(null);

    if (!currentPassword) {
      setPasswordError("Bitte gib dein aktuelles Passwort ein.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("Die Passwörter stimmen nicht überein.");
      return;
    }

    const validation = passwordSchema.safeParse(newPassword);
    if (!validation.success) {
      setPasswordError(validation.error.issues[0]?.message ?? passwordRequirementsText);
      return;
    }

    changeMyPassword({ currentPassword, newPassword });
  };

  const formatTimezoneLabel = (timezone: string) => {
    return timezone.replace(/_/, "/");
  };

  useEffect(() => {
    if (user?.timezone) {
      setSelectedTimezone(user.timezone);
    }
  }, [user]);

  if (!user) {
    return (
      <Content>
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Content>
    );
  }

  return (
    <Content>
      <div className="container mx-auto py-10 max-w-2xl">
        <Card>
          <CardHeader>
            <CardTitle>Benutzereinstellungen</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-medium">Zeitzone</label>
              <Select
                value={selectedTimezone.replace(/\//, "_")}
                onValueChange={setSelectedTimezone}
                disabled={isPending || isTimezoneLocked}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Zeitzone auswählen" />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(Timezone).map((tz) => (
                    <SelectItem value={tz}>{formatTimezoneLabel(tz)}</SelectItem>
                  ))}
                  {/* <SelectItem value="Europe_Berlin">{formatTimezoneLabel("Europe_Berlin")}</SelectItem>
                                    <SelectItem value="Europe_London">{formatTimezoneLabel("Europe_London")}</SelectItem>
                                    <SelectItem value="Europe_Paris">{formatTimezoneLabel("Europe_Paris")}</SelectItem>
                                    <SelectItem value="America_New_York">{formatTimezoneLabel("America_New_York")}</SelectItem>
                                    <SelectItem value="America_Los_Angeles">{formatTimezoneLabel("America_Los_Angeles")}</SelectItem>
                                    <SelectItem value="America_Chicago">{formatTimezoneLabel("America_Chicago")}</SelectItem>
                                    <SelectItem value="Asia_Tokyo">{formatTimezoneLabel("Asia_Tokyo")}</SelectItem>
                                    <SelectItem value="Asia_Dubai">{formatTimezoneLabel("Asia_Dubai")}</SelectItem>
                                    <SelectItem value="Asia_Singapore">{formatTimezoneLabel("Asia_Singapore")}</SelectItem>
                                    <SelectItem value="Australia_Sydney">{formatTimezoneLabel("Australia_Sydney")}</SelectItem>
                                    <SelectItem value="Africa_Cairo">{formatTimezoneLabel("Africa_Cairo")}</SelectItem>
                                    <SelectItem value="Africa_Johannesburg">{formatTimezoneLabel("Africa_Johannesburg")}</SelectItem>
                                    <SelectItem value="America_Sao_Paulo">{formatTimezoneLabel("America_Sao_Paulo")}</SelectItem>
                                    <SelectItem value="America_Buenos_Aires">{formatTimezoneLabel("America_Buenos_Aires")}</SelectItem>
                                    <SelectItem value="Atlantic_Reykjavik">{formatTimezoneLabel("Atlantic_Reykjavik")}</SelectItem> */}
                </SelectContent>
              </Select>

              <span>Dein aktuelles Datum/Uhrzeit ({user.timezone}): <b>{dayjs.tz(new Date(), user.timezone).format('D. MMMM YYYY, HH:mm')} Uhr</b></span>

              {isTimezoneLocked && (
                <p className="text-xs text-muted-foreground">
                  Die Zeitzone kann nur von einem Administrator geändert werden.
                </p>
              )}
            </div>

            <div className="flex justify-end space-x-4 pt-4">
              <Button
                variant="outline"
                onClick={() => navigate("/")}
                disabled={isPending}
              >
                Zurück
              </Button>
              <Button
                onClick={handleSave}
                disabled={isPending || isTimezoneLocked || !selectedTimezone || selectedTimezone === user?.timezone}
              >
                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Speichern
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Passwort ändern</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Aktuelles Passwort</label>
              <Input
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                disabled={isChangingPassword}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Neues Passwort</label>
              <Input
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={isChangingPassword}
              />
              <p className="text-xs text-muted-foreground">{passwordRequirementsText}</p>
              {passwordError && (
                <p className="text-sm text-red-600">{passwordError}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Neues Passwort bestätigen</label>
              <Input
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isChangingPassword}
              />
            </div>

            <div className="flex justify-end pt-2">
              <Button
                onClick={handleChangePassword}
                disabled={
                  isChangingPassword ||
                  !currentPassword ||
                  !newPassword ||
                  !confirmPassword
                }
              >
                {isChangingPassword && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Passwort ändern
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Content>
  );
};

export default UserSettings;
